import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import SectionTitle from "../../components/landing/SectionTitle";
import ChatWidget from "../../components/ChatWidget";
import partnersImage from "../../images/landing/landing_partners.png";
import {
  CONTACTO_URL,
  PARTNERS_DOCS_URL,
  PARTNERS_TITLE,
} from "../../utils/constants";

const BOT_QUICK_ACTIONS = [
  "Cómo me registro como OLS",
  "Qué endpoints tengo que implementar",
  "Cómo funciona la autenticación",
  "Ver guía de integración en 5 pasos",
  "Cómo envío actualizaciones de estado",
  "Tengo un error, necesito ayuda",
  "Cómo hago pruebas en STG",
];

interface IIntegrationStep {
  step: number;
  icon: string;
  title: string;
  description: string;
}

const INTEGRATION_STEPS: IIntegrationStep[] = [
  {
    step: 1,
    icon: "person_add",
    title: "Registrate como partner",
    description:
      "Creá tu cuenta en minutos y accedé al portal de partners con tu entorno de pruebas listo.",
  },
  {
    step: 2,
    icon: "key",
    title: "Obtené tus credenciales",
    description:
      "Generá tus API keys y configurá la autenticación para tu servicio.",
  },
  {
    step: 3,
    icon: "integration_instructions",
    title: "Integrá los endpoints",
    description:
      "Conectá cotizaciones, órdenes y actualizaciones de estado según la documentación.",
  },
  {
    step: 4,
    icon: "rocket_launch",
    title: "¡Salí a producción!",
    description:
      "Pasá a producción y empezá a recibir órdenes reales de marketplaces.",
  },
];

const StepCard = ({ step, icon, title, description }: IIntegrationStep) => (
  <Box
    sx={{
      bgcolor: "grey.50",
      borderRadius: 2,
      p: 3,
      border: "1px solid",
      borderColor: "grey.200",
      height: "100%",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Typography
      sx={{
        fontSize: 80,
        lineHeight: 1,
        position: "absolute",
        top: -8,
        right: 12,
        opacity: 0.06,
        fontWeight: 900,
        color: "primary.main",
        userSelect: "none",
      }}
    >
      {step}
    </Typography>
    <span
      className="material-symbols-rounded color-primary"
      style={{ fontSize: 32, marginBottom: 12, display: "block" }}
    >
      {icon}
    </span>
    <Typography className="f-s-16 text-bold m-b-5">{title}</Typography>
    <Typography className="f-s-14">{description}</Typography>
  </Box>
);

const partnersContent = {
  sectionTitle: PARTNERS_TITLE,
  hero: {
    headline: "Conecta tu operación logística a Coatí",
    subheadline:
      "La plataforma que conecta marketplaces y comercios digitales con operadores logísticos en Latinoamérica.",
    description:
      "Integrá tu servicio una sola vez y empezá a recibir cotizaciones, órdenes y actualizaciones de estado " +
      "dentro de un flujo simple, escalable y con soporte dedicado.",
    benefits: [
      "Integración por API para operadores logísticos de Latinoamérica",
      "Recepción de cotizaciones y órdenes en tu propio sistema",
      "Actualización de estados vía webhook en tiempo real",
      "Onboarding asistido con documentación y soporte técnico regional",
    ],
    ctaPrimary: { label: "Ver documentación técnica", url: PARTNERS_DOCS_URL },
  },
  steps: {
    headline: "¿Cómo funciona la integración?",
    description:
      "Cuatro pasos para conectar tu operación logística a nuestra plataforma.",
  },
  bot: {
    headline: "¿Dudas con la integración? CoatiBot te ayuda ahora",
    description:
      "Nuestro asistente técnico responde en tiempo real sobre endpoints, credenciales, " +
      "errores y el flujo completo de implementación. Elegí una pregunta o escribí la tuya.",
    cta: "Hablar con CoatiBot",
  },
  contact: {
    text: "¿Preferís hablar con alguien del equipo?",
    linkLabel: "Contactanos directamente",
  },
};

// eslint-disable-next-line max-lines-per-function
const Partners = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | undefined>();

  useEffect(() => {
    const timer = setTimeout(() => setChatOpen(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const handleQuickAction = (message: string) => {
    setPendingMessage(message);
    setChatOpen(true);
  };

  return (
    <>
      <Grid
        container
        item
        className="p-x-landing p-y-landing"
        justifyContent="center"
        marginBottom={{ xs: 10, lg: 20 }}
      >
        <SectionTitle title={partnersContent.sectionTitle} marginBottom={10} />

        {/* Hero */}
        <Grid
          item
          container
          alignItems="center"
          columnSpacing={{ xs: 5, lg: 15 }}
          rowSpacing={{ xs: 5, lg: 0 }}
          marginBottom={{ xs: 8, lg: 14 }}
        >
          <Grid item xs={12} lg={6} textAlign="center">
            <img
              src={partnersImage}
              className="w-100 br-10"
              alt="Partners integración logística"
            />
          </Grid>

          <Grid item container flexDirection="column" xs={12} lg={6} gap={2.5}>
            <Typography className="f-s-20 f-s-md-32 color-primary text-bold">
              {partnersContent.hero.headline}
            </Typography>
            <Typography className="f-s-16 f-s-md-22 text-bold">
              {partnersContent.hero.subheadline}
            </Typography>
            <Typography className="f-s-14 f-s-md-18">
              {partnersContent.hero.description}
            </Typography>

            <List disablePadding>
              {partnersContent.hero.benefits.map((benefit) => (
                <ListItem key={benefit} disablePadding className="m-b-10">
                  <ListItemIcon>
                    <span className="material-symbols-rounded color-primary">
                      check
                    </span>
                  </ListItemIcon>
                  <ListItemText
                    classes={{ primary: "f-s-14 f-s-md-18" }}
                    primary={benefit}
                  />
                </ListItem>
              ))}
            </List>

            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              gap={2}
              mt={1}
            >
              <Button
                variant="contained"
                color="primary"
                href={partnersContent.hero.ctaPrimary.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {partnersContent.hero.ctaPrimary.label}
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Integration journey stepper */}
        <Grid item xs={12} marginBottom={{ xs: 8, lg: 14 }}>
          <Typography
            className="f-s-18 f-s-md-28 text-bold m-b-10"
            textAlign="center"
          >
            {partnersContent.steps.headline}
          </Typography>
          <Typography
            className="f-s-14 f-s-md-18"
            textAlign="center"
            sx={{ mb: 5 }}
          >
            {partnersContent.steps.description}
          </Typography>
          <Grid container spacing={3}>
            {INTEGRATION_STEPS.map((step) => (
              <Grid item xs={12} sm={6} lg={3} key={step.step}>
                <StepCard {...step} />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Staging test environment section */}
        <Grid item xs={12} marginBottom={{ xs: 8, lg: 14 }}>
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              p: { xs: 3, lg: 5 },
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <span
              className="material-symbols-rounded color-primary"
              style={{ fontSize: 36, display: "block", marginBottom: 12 }}
            >
              science
            </span>
            <Typography className="f-s-18 f-s-md-28 text-bold m-b-10">
              Probá en staging antes de ir a producción
            </Typography>
            <Typography className="f-s-14 f-s-md-18" sx={{ mb: 4 }}>
              El entorno de staging te permite validar el flujo completo de cotización,
              creación de órdenes y devoluciones antes de salir a producción.
            </Typography>

            <Grid container spacing={3}>
              {/* Docs link */}
              <Grid item xs={12}>
                <Typography className="f-s-16 text-bold m-b-5">
                  Documentación técnica (Swagger UI)
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  href="https://api-stg.coatiapp.com/auth/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={
                    <span className="material-symbols-rounded">open_in_new</span>
                  }
                >
                  https://api-stg.coatiapp.com/auth/docs
                </Button>
              </Grid>

              {/* Credentials */}
              <Grid item xs={12} md={6}>
                <Typography className="f-s-16 text-bold m-b-10">
                  Credenciales del marketplace de prueba
                </Typography>
                <Box
                  sx={{
                    bgcolor: "grey.100",
                    borderRadius: 1,
                    p: 2,
                    fontFamily: "monospace",
                    fontSize: 13,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Box>
                    <strong>apikey:</strong> 87c7141d-deea-4a48-b7d0-daac7239eff0
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <strong>merchantCuit:</strong> 34606200066
                  </Box>
                </Box>
                <Typography className="f-s-12" sx={{ mt: 1, color: "text.secondary" }}>
                  Credenciales compartidas para ejecutar requests desde el Swagger UI.
                  No corresponden a tu api_key de registro.
                </Typography>
              </Grid>

              {/* Activation steps */}
              <Grid item xs={12} md={6}>
                <Typography className="f-s-16 text-bold m-b-5">
                  Proceso de activación para pruebas transaccionales
                </Typography>
                <List disablePadding dense>
                  {[
                    "Registrate vía POST /external_carriers y guardá tu api_key.",
                    "Implementá tus endpoints /quotes y /orders/create.",
                    "Avisá al equipo de Coati con tu api_key de prueba.",
                    "Coati la configura a nivel seller (es el api-token que enviamos a tus endpoints). En producción equivale al código de contrato seller-operador.",
                    "Con el operador activo, usá las credenciales del marketplace en el Swagger para probar cotización, creación y devoluciones.",
                  ].map((text, i) => (
                    <ListItem key={i} disablePadding sx={{ mb: 1, alignItems: "flex-start" }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                        <Typography className="color-primary text-bold f-s-14">
                          {i + 1}.
                        </Typography>
                      </ListItemIcon>
                      <ListItemText
                        classes={{ primary: "f-s-14" }}
                        primary={text}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Bot — protagonist section */}
        <Grid item xs={12}>
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              p: { xs: 3, lg: 5 },
              border: "1px solid",
              borderColor: "grey.200",
              textAlign: "center",
            }}
          >
            <span
              className="material-symbols-rounded color-primary"
              style={{ fontSize: 48, display: "block", marginBottom: 12 }}
            >
              forum
            </span>
            <Typography className="f-s-20 f-s-md-32 text-bold m-b-10">
              {partnersContent.bot.headline}
            </Typography>
            <Typography
              className="f-s-14 f-s-md-18"
              sx={{ maxWidth: 580, mx: "auto", mb: 4 }}
            >
              {partnersContent.bot.description}
            </Typography>

            <Box
              display="flex"
              flexWrap="wrap"
              gap={1.5}
              justifyContent="center"
              mb={4}
            >
              {BOT_QUICK_ACTIONS.map((action) => (
                <Chip
                  key={action}
                  label={action}
                  variant="outlined"
                  color="primary"
                  clickable
                  onClick={() => handleQuickAction(action)}
                />
              ))}
            </Box>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setChatOpen(true)}
              startIcon={<span className="material-symbols-rounded">chat</span>}
            >
              {partnersContent.bot.cta}
            </Button>

            <Typography className="f-s-14" sx={{ mt: 3 }}>
              {partnersContent.contact.text}{" "}
              <Link href={CONTACTO_URL}>
                {partnersContent.contact.linkLabel}
              </Link>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <ChatWidget
        isOpen={chatOpen}
        onToggle={() => setChatOpen((v) => !v)}
        initialMessage={pendingMessage}
        onMessageSent={() => setPendingMessage(undefined)}
      />
    </>
  );
};

export default Partners;
